import { timeConversion } from "client-app-core";
import { getTranslation } from "orion-components/i18n";

const sanitizeSnapshotFileName = value => {
	const normalized = (value || "camera").toString().trim();
	return normalized
		.replace(/[<>:"/\\|?*\x00-\x1F]/g, "")
		.replace(/\s+/g, "_")
		.slice(0, 80);
};

/**
 * Formats and sanitizes a timestamp for use in filenames
 * @param {string} timeFormat - Either "12-hour" or "24-hour"
 * @param {string} locale - User's locale setting
 * @returns {string} Sanitized timestamp safe for filenames
 */
const getFormattedTimestampForFilename = (timeFormat = "12-hour", locale = "en") => {
	try {
		// Get user's formatted time with timezone
		const format = timeFormat === "24-hour" ? "full_24-hour" : "full_12-hour";
		// Moment.js doesn't support "ar_kw" locale, fallback to "ar"
		const localeOverride = locale === "ar_kw" ? "ar" : locale;
		const userTime = timeConversion.convertToUserTime(new Date().toISOString(), format, localeOverride);

		// Sanitize userTime for filesystem safety - preserve Arabic-Indic numerals (٠-٩)
		const sanitizedTime = userTime
			.replace(/[\/\\:*?"<>|]/g, "-")
			.replace(/\s+/g, "_")
			.replace(/[^\w\-_.٠-٩]/g, "");

		return sanitizedTime;
	} catch (error) {
		// Fallback to ISO format if formatting fails
		return new Date().toISOString().replace(/[.:]/g, "-");
	}
};

const downloadSnapshotFile = ({ sourceUrl, camera, revokeObjectUrl = false, timeFormat, locale }) => {
	const timestamp = getFormattedTimestampForFilename(timeFormat, locale);
	const cameraName = sanitizeSnapshotFileName(camera?.entityData?.properties?.name || camera?.id || "camera");
	const downloadLink = document.createElement("a");
	downloadLink.href = sourceUrl;
	downloadLink.download = `${cameraName}-${getTranslation("global.profiles.widgets.cameras.camCard.snapshot")}-${timestamp}.jpg`;
	document.body.appendChild(downloadLink);
	downloadLink.click();
	document.body.removeChild(downloadLink);

	if (revokeObjectUrl) {
		setTimeout(() => URL.revokeObjectURL(sourceUrl), 0);
	}
};

export const handleSnapshotCapture = ({
	camera,
	mainMenuRef,
	supportsClientSnapshot,
	supportsServerSnapshot,
	cameraService,
	dispatch,
	createUserFeedback,
	getTranslation,
	timeFormat = "12-hour",
	locale = "en"
}) => {
	if (!supportsClientSnapshot) {
		if (supportsServerSnapshot) {
			cameraService.getSnapshot(camera.id, (err, response) => {
				if (err || !response) {
					const errorMessage =
						(err && err.message) ||
						(response && response.message) ||
						getTranslation("global.profiles.widgets.cameras.camCard.userFeedback.snapshotFailed");
					dispatch(createUserFeedback(errorMessage, null, true));
					return;
				}

				const base64Image = response?.image?.Data || response?.image?.data || response?.image;
				if (!base64Image || typeof base64Image !== "string") {
					dispatch(
						createUserFeedback(
							getTranslation(
								"global.profiles.widgets.cameras.camCard.userFeedback.snapshotInvalidPayload"
							),
							null,
							true
						)
					);
					return;
				}

				const snapshotUrl = base64Image.startsWith("data:")
					? base64Image
					: `data:image/jpeg;base64,${base64Image}`;

				downloadSnapshotFile({ sourceUrl: snapshotUrl, camera, timeFormat, locale });
				dispatch(
					createUserFeedback(
						getTranslation("global.profiles.widgets.cameras.camCard.userFeedback.snapshotSuccess"),
						null,
						false
					)
				);
			});
		}
		console.error("Snapshot capture is not supported for this camera.");
		return;
	}

	const playerContainer = mainMenuRef.current?.closest(".bccVideoPlayerContainer");

	// WS-JPEG streams require a different approach since they use an <img> tag instead of <video>
	if (camera?.player?.type === "ws-jpeg") {
		const imageFrame = playerContainer?.querySelector("img");
		const imageSrc = imageFrame?.getAttribute("src") || "";

		if (!imageFrame || !imageFrame.complete || imageFrame.naturalWidth <= 0 || !imageSrc) {
			dispatch(
				createUserFeedback(
					getTranslation("global.profiles.widgets.cameras.camCard.userFeedback.snapshotVideoNotReady"),
					null,
					true
				)
			);
			return;
		}

		downloadSnapshotFile({ sourceUrl: imageSrc, camera, timeFormat, locale });
		dispatch(
			createUserFeedback(
				getTranslation("global.profiles.widgets.cameras.camCard.userFeedback.snapshotSuccess"),
				null,
				false
			)
		);
		return;
	}

	const video = playerContainer?.querySelector("video");

	if (!video) {
		dispatch(
			createUserFeedback(
				getTranslation("global.profiles.widgets.cameras.camCard.userFeedback.snapshotNoVideo"),
				null,
				true
			)
		);
		return;
	}

	if (video.readyState < 2 || video.videoWidth <= 0 || video.videoHeight <= 0) {
		dispatch(
			createUserFeedback(
				getTranslation("global.profiles.widgets.cameras.camCard.userFeedback.snapshotVideoNotReady"),
				null,
				true
			)
		);
		return;
	}

	try {
		const canvas = document.createElement("canvas");
		canvas.width = video.videoWidth;
		canvas.height = video.videoHeight;

		const context = canvas.getContext("2d");
		if (!context) {
			dispatch(
				createUserFeedback(
					getTranslation("global.profiles.widgets.cameras.camCard.userFeedback.snapshotInitFailed"),
					null,
					true
				)
			);
			return;
		}

		context.drawImage(video, 0, 0);

		canvas.toBlob(
			blob => {
				if (!blob) {
					dispatch(
						createUserFeedback(
							getTranslation("global.profiles.widgets.cameras.camCard.userFeedback.snapshotExportFailed"),
							null,
							true
						)
					);
					return;
				}

				const blobUrl = URL.createObjectURL(blob);
				downloadSnapshotFile({ sourceUrl: blobUrl, camera, revokeObjectUrl: true, timeFormat, locale });

				dispatch(
					createUserFeedback(
						getTranslation("global.profiles.widgets.cameras.camCard.userFeedback.snapshotSuccess"),
						null,
						false
					)
				);
			},
			"image/jpeg",
			0.92
		);
	} catch (error) {
		const message =
			error?.message && error.message.toLowerCase().includes("tainted")
				? getTranslation("global.profiles.widgets.cameras.camCard.userFeedback.snapshotCorsFailed")
				: `Snapshot capture failed: ${error?.message || getTranslation("global.profiles.widgets.cameras.camCard.userFeedback.snapshotUnknownFailed")}`;
		dispatch(createUserFeedback(message, null, true));
	}
};
